import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { ArrowLeft, Loader2, BarChart3, Save, PieChart, LineChart, Table, Download } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReportingSettingsPage = () => {
  const [settings, setSettings] = useState({
    // Dashboard settings
    default_dashboard_period: '30',
    show_participant_stats: true,
    show_case_stats: true,
    show_enrollment_stats: true,
    show_communication_stats: true,
    
    // Report access
    report_access_roles: ['system_admin', 'executive', 'operations_manager'],
    allow_data_export: true,
    export_formats: ['csv', 'excel', 'pdf'],
    
    // KPIs
    kpi_target_caseload: 50,
    kpi_target_response_time: 24,
    kpi_target_note_frequency: 7,
    
    // Scheduled reports
    scheduled_reports_enabled: false,
    report_schedule: 'weekly',
    report_recipients: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/reporting-settings`);
      if (response.data) {
        setSettings(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error('Failed to fetch reporting settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/reporting-settings`, settings);
      toast.success('Reporting settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const toggleRole = (role) => {
    const roles = settings.report_access_roles || [];
    if (roles.includes(role)) {
      setSettings({ ...settings, report_access_roles: roles.filter(r => r !== role) });
    } else {
      setSettings({ ...settings, report_access_roles: [...roles, role] });
    }
  };

  const toggleFormat = (format) => {
    const formats = settings.export_formats || [];
    if (formats.includes(format)) {
      setSettings({ ...settings, export_formats: formats.filter(f => f !== format) });
    } else {
      setSettings({ ...settings, export_formats: [...formats, format] });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reporting-settings-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/settings">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Settings
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <BarChart3 className="w-6 h-6" />
              Reporting & Dashboards
            </h1>
            <p className="text-muted-foreground">Configure reports, KPIs, and dashboard settings</p>
          </div>
        </div>
        <Button onClick={saveSettings} disabled={saving}>
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save Settings
        </Button>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Dashboard Widgets */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PieChart className="w-5 h-5" />
              Dashboard Widgets
            </CardTitle>
            <CardDescription>Configure which widgets appear on the dashboard</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Default Time Period</Label>
              <Select
                value={settings.default_dashboard_period}
                onValueChange={(v) => setSettings({ ...settings, default_dashboard_period: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">Last 7 Days</SelectItem>
                  <SelectItem value="30">Last 30 Days</SelectItem>
                  <SelectItem value="90">Last 90 Days</SelectItem>
                  <SelectItem value="365">Last Year</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-3 pt-2">
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <span>Participant Statistics</span>
                <Switch
                  checked={settings.show_participant_stats}
                  onCheckedChange={(v) => setSettings({ ...settings, show_participant_stats: v })}
                />
              </div>
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <span>Case Statistics</span>
                <Switch
                  checked={settings.show_case_stats}
                  onCheckedChange={(v) => setSettings({ ...settings, show_case_stats: v })}
                />
              </div>
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <span>Enrollment Statistics</span>
                <Switch
                  checked={settings.show_enrollment_stats}
                  onCheckedChange={(v) => setSettings({ ...settings, show_enrollment_stats: v })}
                />
              </div>
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <span>Communication Statistics</span>
                <Switch
                  checked={settings.show_communication_stats}
                  onCheckedChange={(v) => setSettings({ ...settings, show_communication_stats: v })}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* KPI Targets */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <LineChart className="w-5 h-5" />
              KPI Targets
            </CardTitle>
            <CardDescription>Set performance targets and thresholds</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Target Caseload per Worker</Label>
              <Input
                type="number"
                value={settings.kpi_target_caseload}
                onChange={(e) => setSettings({ ...settings, kpi_target_caseload: parseInt(e.target.value) || 50 })}
              />
              <p className="text-xs text-muted-foreground">Ideal number of active cases per case manager</p>
            </div>

            <div className="space-y-2">
              <Label>Target Response Time (hours)</Label>
              <Input
                type="number"
                value={settings.kpi_target_response_time}
                onChange={(e) => setSettings({ ...settings, kpi_target_response_time: parseInt(e.target.value) || 24 })}
              />
              <p className="text-xs text-muted-foreground">Maximum hours to respond to participant communications</p>
            </div>

            <div className="space-y-2">
              <Label>Case Note Frequency (days)</Label>
              <Input
                type="number"
                value={settings.kpi_target_note_frequency}
                onChange={(e) => setSettings({ ...settings, kpi_target_note_frequency: parseInt(e.target.value) || 7 })}
              />
              <p className="text-xs text-muted-foreground">Maximum days between case notes</p>
            </div>
          </CardContent>
        </Card>

        {/* Report Access */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Table className="w-5 h-5" />
              Report Access
            </CardTitle>
            <CardDescription>Configure who can access reports</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="mb-2 block">Roles with Report Access</Label>
              <div className="flex flex-wrap gap-2">
                {['system_admin', 'executive', 'operations_manager', 'case_manager', 'case_worker'].map((role) => (
                  <Badge 
                    key={role} 
                    variant={settings.report_access_roles?.includes(role) ? 'default' : 'outline'}
                    className="cursor-pointer"
                    onClick={() => toggleRole(role)}
                  >
                    {role.replace(/_/g, ' ')}
                  </Badge>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-2">Click to toggle access</p>
            </div>

            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <p className="font-medium">Allow Data Export</p>
                <p className="text-sm text-muted-foreground">Users can download report data</p>
              </div>
              <Switch
                checked={settings.allow_data_export}
                onCheckedChange={(v) => setSettings({ ...settings, allow_data_export: v })}
              />
            </div>

            {settings.allow_data_export && (
              <div>
                <Label className="mb-2 block">Export Formats</Label>
                <div className="flex flex-wrap gap-2">
                  {['csv', 'excel', 'pdf'].map((format) => (
                    <Badge 
                      key={format} 
                      variant={settings.export_formats?.includes(format) ? 'default' : 'outline'}
                      className="cursor-pointer uppercase"
                      onClick={() => toggleFormat(format)}
                    >
                      {format}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Scheduled Reports */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Download className="w-5 h-5" />
              Scheduled Reports
            </CardTitle>
            <CardDescription>Automatically send reports via email</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <p className="font-medium">Enable Scheduled Reports</p>
                <p className="text-sm text-muted-foreground">Automatically email reports</p>
              </div>
              <Switch
                checked={settings.scheduled_reports_enabled}
                onCheckedChange={(v) => setSettings({ ...settings, scheduled_reports_enabled: v })}
              />
            </div>

            {settings.scheduled_reports_enabled && (
              <>
                <div className="space-y-2">
                  <Label>Report Schedule</Label>
                  <Select
                    value={settings.report_schedule}
                    onValueChange={(v) => setSettings({ ...settings, report_schedule: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Recipients (comma-separated emails)</Label>
                  <Input
                    value={settings.report_recipients}
                    onChange={(e) => setSettings({ ...settings, report_recipients: e.target.value })}
                    placeholder="admin@example.com, manager@example.com"
                  />
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ReportingSettingsPage;
